import { animationDuration, animationFrameId, documentWithAnimationAtTime } from "@/document/animation/applyAnimation";
import { findFirstFrameNode, findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import { oklchToRgba } from "@/document/model/color";
import type { FrameNode, PrimitiveNode, SceneNode, TextNode, VectorNetworkNode } from "@/document/model/nodes";
import { normalizeFillStack, normalizeStrokeStack, type Paint, type StrokeStyle } from "@/document/model/styles";
import { resolvedLineHeight, type TypeStyle } from "@/document/model/text";
import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { pathDataToVectorNetwork } from "@/document/operations/pathDataToVectorNetwork";
import { primitiveToPolygon } from "@/document/operations/primitiveGeometry";
import { vectorRegionGeometries } from "@/document/operations/vectorRegions";
import { stableJsonStringify } from "@/io/package";
import { transformRotationOrigin } from "@/lib/math/rotation";
import { textNodeToSvgPaths } from "./textVectorize";

export interface LottieExportOptions {
  document: CurvaDocument;
  frameId?: string;
  rootNodeId?: string;
  fps?: number;
  animated?: boolean;
  onProgress?: (progress: number, frame: number, totalFrames: number) => void;
  signal?: AbortSignal;
}

export interface LottieExportResult {
  json: string;
  lottie: LottieDocument;
  durationMs: number;
  frames: number;
  frameName: string;
  unsupportedNodeIds: string[];
}

interface LottieDocument {
  v: string;
  fr: number;
  ip: number;
  op: number;
  w: number;
  h: number;
  nm: string;
  ddd: 0;
  assets: [];
  fonts?: {
    list: LottieFont[];
  };
  layers: LottieLayer[];
  meta: {
    g: string;
    a: string;
    unsupportedNodeIds?: string[];
  };
}

type LottieLayer = LottieShapeLayer | LottieTextLayer;

interface LottieFont {
  fName: string;
  fFamily: string;
  fStyle: string;
  ascent: number;
}

type LottieScalarAnimatable = { a: 0; k: number } | { a: 1; k: LottieKeyframe<number[]>[] };
type LottieVectorAnimatable = { a: 0; k: number[] } | { a: 1; k: LottieKeyframe<number[]>[] };
type LottiePathAnimatable = { a: 0; k: LottiePathValue } | { a: 1; k: LottieKeyframe<LottiePathValue[]>[] };

interface LottieKeyframe<T> {
  t: number;
  s: T;
  e?: T;
  h?: 1;
  i?: { x: number[]; y: number[] };
  o?: { x: number[]; y: number[] };
}

interface LottieShapeLayer {
  ddd: 0;
  ind: number;
  ty: 4;
  nm: string;
  td?: 1;
  tt?: 1;
  hd?: boolean;
  sr: 1;
  ks: {
    o: LottieScalarAnimatable;
    r: LottieScalarAnimatable;
    p: LottieVectorAnimatable;
    a: LottieVectorAnimatable;
    s: LottieVectorAnimatable;
  };
  ao: 0;
  shapes: LottieShapeItem[];
  ip: number;
  op: number;
  st: 0;
  bm: 0;
}

interface LottieTextLayer {
  ddd: 0;
  ind: number;
  ty: 5;
  nm: string;
  sr: 1;
  ks: {
    o: LottieScalarAnimatable;
    r: LottieScalarAnimatable;
    p: LottieVectorAnimatable;
    a: LottieVectorAnimatable;
    s: LottieVectorAnimatable;
  };
  ao: 0;
  t: {
    d: {
      k: Array<{
        s: LottieTextDocument;
        t: 0;
      }>;
    };
    p: Record<string, never>;
    m: {
      g: 1;
      a: LottieVectorAnimatable;
    };
  };
  ip: number;
  op: number;
  st: 0;
  bm: 0;
}

interface LottieTextDocument {
  s: number;
  f: string;
  t: string;
  j: number;
  tr: number;
  lh: number;
  ls: number;
  fc: number[];
  sz: number[];
  ps: number[];
}

type LottieShapeItem =
  | LottieGroupShape
  | LottieShapeContentItem;

type LottieShapeContentItem =
  | LottieEllipseShape
  | LottieRectangleShape
  | LottiePathShape
  | LottieFillShape
  | LottieStrokeShape
  | LottieTransformShape;

interface LottieGroupShape {
  ty: "gr";
  nm: string;
  it: LottieShapeContentItem[];
}

interface LottieEllipseShape {
  ty: "el";
  nm: string;
  p: LottieVectorAnimatable;
  s: LottieVectorAnimatable;
  d: 1;
}

interface LottieRectangleShape {
  ty: "rc";
  nm: string;
  p: LottieVectorAnimatable;
  s: LottieVectorAnimatable;
  r: LottieScalarAnimatable;
  d: 1;
}

interface LottiePathShape {
  ty: "sh";
  nm: string;
  ks: LottiePathAnimatable;
}

interface LottieFillShape {
  ty: "fl";
  nm: string;
  c: LottieVectorAnimatable;
  o: LottieScalarAnimatable;
  r: 1 | 2;
}

interface LottieStrokeShape {
  ty: "st";
  nm: string;
  c: LottieVectorAnimatable;
  o: LottieScalarAnimatable;
  w: LottieScalarAnimatable;
  lc: 2;
  lj: 2;
}

interface LottieTransformShape {
  ty: "tr";
  p: LottieVectorAnimatable;
  a: LottieVectorAnimatable;
  s: LottieVectorAnimatable;
  r: LottieScalarAnimatable;
  o: LottieScalarAnimatable;
  sk: LottieScalarAnimatable;
  sa: LottieScalarAnimatable;
}

interface LottiePathValue {
  i: number[][];
  o: number[][];
  v: number[][];
  c: boolean;
}

interface LottieSample {
  frameIndex: number;
  frame: FrameNode;
}

interface RenderSample {
  frameIndex: number;
  shape?: LottieShapeGeometry;
  opacity: number;
  rotation: number;
  rotationOrigin: number[];
  fillColor?: number[];
  fillOpacity: number;
  strokeColor?: number[];
  strokeOpacity: number;
  strokeWidth: number;
}

type LottieShapeGeometry =
  | { kind: "ellipse"; center: number[]; size: number[] }
  | { kind: "rectangle"; center: number[]; size: number[]; radius: number }
  | { kind: "path"; paths: LottiePathValue[]; strokePaths?: LottiePathValue[]; fillRule?: "nonzero" | "evenodd" }
  | { kind: "text"; text: string; style: TypeStyle; boxPosition: number[]; boxSize: number[] };

interface LayerTrack {
  id: string;
  name: string;
  kind: LottieShapeGeometry["kind"];
  samples: RenderSample[];
  matteTrack?: LayerTrack;
}

interface VisibleNodeEntry {
  id: string;
  name: string;
  sample: RenderSample;
  matte?: {
    id: string;
    name: string;
    sample: RenderSample;
  };
}

const DEFAULT_FPS = 30;
const STATIC_DURATION_FRAMES = 1;
const LOTTIE_VERSION = "5.12.2";
const LINEAR_IN = { x: [1], y: [1] };
const LINEAR_OUT = { x: [0], y: [0] };

export async function exportDocumentToLottie(options: LottieExportOptions): Promise<LottieExportResult> {
  const fps = normalizedFps(options.fps);
  const animated = options.animated ?? Boolean(options.rootNodeId || options.document.animation?.nodes.length);
  const baseFrame = resolveBaseFrame(options.document, options.frameId, options.rootNodeId);
  const durationMs = animated ? animationDuration(options.document.animation, options.rootNodeId, options.document) : 0;
  const samples = animated && durationMs > 0
    ? sampleAnimatedFrames(options, fps, durationMs)
    : [{ frameIndex: 0, frame: baseFrame }];
  const totalFrames = Math.max(STATIC_DURATION_FRAMES, samples.length);
  const tracks = await collectLayerTracks(samples, baseFrame);
  const unsupportedNodeIds = [...new Set(tracks.unsupportedNodeIds)].sort();

  const width = Math.max(1, Math.ceil(Math.abs(baseFrame.bounds.width)));
  const height = Math.max(1, Math.ceil(Math.abs(baseFrame.bounds.height)));
  const lottie: LottieDocument = {
    v: LOTTIE_VERSION,
    fr: fps,
    ip: 0,
    op: totalFrames,
    w: width,
    h: height,
    nm: baseFrame.name || "Curva Lottie",
    ddd: 0,
    assets: [],
    ...(lottieFonts(tracks.layers).length > 0 ? { fonts: { list: lottieFonts(tracks.layers) } } : {}),
    layers: lottieLayers(tracks.layers, totalFrames),
    meta: {
      g: "Curva",
      a: animated && durationMs > 0
        ? "Native Lottie shape layers sampled from Curva animation solver"
        : "Native Lottie shape layers exported from Curva",
      ...(unsupportedNodeIds.length ? { unsupportedNodeIds } : {}),
    },
  };

  return {
    json: stableJsonStringify(lottie),
    lottie,
    durationMs,
    frames: samples.length,
    frameName: baseFrame.name || "Animation",
    unsupportedNodeIds,
  };
}

function sampleAnimatedFrames(options: LottieExportOptions, fps: number, durationMs: number): LottieSample[] {
  const totalFrames = Math.max(1, Math.floor((durationMs / 1000) * fps) + 1);
  const samples: LottieSample[] = [];

  for (let frame = 0; frame < totalFrames; frame += 1) {
    throwIfAborted(options.signal);
    const timeMs = frameTimeMs(frame, fps, durationMs);
    const evaluation = documentWithAnimationAtTime(options.document, timeMs, options.rootNodeId);
    const frameId = evaluation.displayFrameId ?? animationFrameId(evaluation.document.animation, options.rootNodeId) ?? options.frameId;
    samples.push({ frameIndex: frame, frame: resolveFrame(evaluation.document, frameId) });
    options.onProgress?.((frame + 1) / totalFrames, frame + 1, totalFrames);
  }

  return samples;
}

async function collectLayerTracks(samples: LottieSample[], baseFrame: FrameNode): Promise<{ layers: LayerTrack[]; unsupportedNodeIds: string[] }> {
  const byId = new Map<string, LayerTrack>();
  const order: string[] = (await visibleEntriesForFrame(baseFrame, baseFrame, [])).map(entry => entry.id);
  const orderedIds = new Set(order);
  const unsupportedNodeIds: string[] = [];
  const totalFrames = samples.length;

  for (const sample of samples) {
    const sampleEntries = await visibleEntriesForFrame(sample.frame, baseFrame, unsupportedNodeIds);
    for (const entry of sampleEntries) {
      let track = byId.get(entry.id);
      if (!track) {
        track = {
          id: entry.id,
          name: entry.name,
          kind: entry.sample.shape?.kind ?? "rectangle",
          samples: Array.from({ length: totalFrames }, (_, index) => emptySample(index)),
        };
        byId.set(entry.id, track);
        if (!orderedIds.has(entry.id)) {
          order.push(entry.id);
          orderedIds.add(entry.id);
        }
      }
      track.samples[sample.frameIndex] = entry.sample;
      if (entry.matte) {
        const matteId = `matte:${entry.matte.id}:for:${entry.id}`;
        track.matteTrack ??= {
          id: matteId,
          name: `${entry.matte.name} matte for ${entry.name}`,
          kind: entry.matte.sample.shape?.kind ?? "path",
          samples: Array.from({ length: totalFrames }, (_, index) => emptySample(index)),
        };
        track.matteTrack.samples[sample.frameIndex] = entry.matte.sample;
      }
    }
  }

  const layers = order
    .flatMap(id => byId.get(id) ?? [])
    .filter(track => track.samples.some(sample => sample.shape && sample.opacity > 0.0001));
  return { layers: layers.reverse(), unsupportedNodeIds };
}

async function visibleEntriesForFrame(
  frame: FrameNode,
  baseFrame: FrameNode,
  unsupportedNodeIds: string[],
): Promise<VisibleNodeEntry[]> {
  const entries: VisibleNodeEntry[] = [];
  const frameFill = firstSolidPaint(normalizeFillStack(frame.style));
  if (frame.visible && frameFill) {
    entries.push({
      id: frame.id,
      name: frame.name || "Frame background",
      sample: nodeSample(frame, baseFrame, {
        kind: "rectangle",
        center: centerForBounds(frame.bounds, baseFrame),
        size: [Math.abs(frame.bounds.width), Math.abs(frame.bounds.height)],
        radius: 0,
      }),
    });
  }

  await collectVisibleNodeEntries(frame.children, baseFrame, true, entries, unsupportedNodeIds);
  return entries;
}

async function collectVisibleNodeEntries(
  nodes: SceneNode[],
  baseFrame: FrameNode,
  ancestorVisible: boolean,
  entries: VisibleNodeEntry[],
  unsupportedNodeIds: string[],
  activeMask?: SceneNode,
): Promise<void> {
  let currentMask = activeMask;
  for (const node of nodes) {
    const visible = ancestorVisible && node.visible;
    if (node.isMask) {
      currentMask = visible ? node : undefined;
      continue;
    }
    const children = getSceneNodeChildren(node);
    if (children) {
      await collectVisibleNodeEntries(children, baseFrame, visible, entries, unsupportedNodeIds, currentMask);
    }
    if (!visible) {
      continue;
    }
    if (node.type === "text") {
      const textEntries = await textEntriesForNode(node, baseFrame, currentMask, unsupportedNodeIds);
      entries.push(...textEntries);
      continue;
    }
    const geometry = nodeGeometry(node, baseFrame);
    if (!geometry) {
      if (isPotentiallyVisibleLeaf(node)) {
        unsupportedNodeIds.push(node.id);
      }
      continue;
    }
    const matte = currentMask ? matteEntryForMask(currentMask, baseFrame, unsupportedNodeIds) : undefined;
    entries.push({ id: node.id, name: node.name || node.id, sample: nodeSample(node, baseFrame, geometry), ...(matte ? { matte } : {}) });
  }
}

async function textEntriesForNode(
  node: TextNode,
  frame: FrameNode,
  activeMask: SceneNode | undefined,
  unsupportedNodeIds: string[],
): Promise<VisibleNodeEntry[]> {
  const paths = await textNodeToSvgPaths(node);
  const entries: VisibleNodeEntry[] = [];
  for (const [index, path] of paths.entries()) {
    const geometry = svgPathDataToLottiePaths(path.d, frame);
    if (geometry.length === 0) {
      continue;
    }
    const shape: LottieShapeGeometry = { kind: "path", paths: geometry, fillRule: "nonzero" };
    const matte = activeMask ? matteEntryForMask(activeMask, frame, unsupportedNodeIds) : undefined;
    entries.push({
      id: `${node.id}:text:${index}`,
      name: paths.length > 1 ? `${node.name || node.id} ${index + 1}` : node.name || node.id,
      sample: nodeSample(node, frame, shape, { fill: path.fill }),
      ...(matte ? { matte } : {}),
    });
  }
  if (entries.length === 0) {
    const fallbackShape = textFallbackGeometry(node, frame);
    const matte = activeMask ? matteEntryForMask(activeMask, frame, unsupportedNodeIds) : undefined;
    entries.push({
      id: node.id,
      name: node.name || node.id,
      sample: nodeSample(node, frame, fallbackShape, { fill: node.text.style.fill }),
      ...(matte ? { matte } : {}),
    });
  }
  return entries;
}

export function svgPathDataToLottiePaths(pathData: string, frame: FrameNode): LottiePathValue[] {
  const network = pathDataToVectorNetwork(pathData, { reuseVertices: false });
  return vectorNetworkStrokePaths(network, frame);
}

function textFallbackGeometry(node: TextNode, frame: FrameNode): LottieShapeGeometry {
  return {
    kind: "text",
    text: node.text.characters,
    style: node.text.style,
    boxPosition: [node.bounds.x - frame.bounds.x, node.bounds.y - frame.bounds.y],
    boxSize: [Math.abs(node.bounds.width), Math.abs(node.bounds.height)],
  };
}

function nodeGeometry(node: SceneNode, frame: FrameNode): LottieShapeGeometry | undefined {
  if (node.type === "primitive") {
    return primitiveGeometry(node, frame);
  }
  if (node.type === "vectorNetwork") {
    const paths = vectorNetworkPaths(node.network, frame);
    const strokePaths = vectorNetworkStrokePaths(node.network, frame);
    if (paths.length > 0) {
      return { kind: "path", paths, ...(strokePaths.length > 0 ? { strokePaths } : {}), fillRule: node.network.fillRule };
    }
    return strokePaths.length > 0 ? { kind: "path", paths: strokePaths, strokePaths, fillRule: node.network.fillRule } : undefined;
  }
  return undefined;
}

function nodeMaskGeometry(node: SceneNode, frame: FrameNode): LottieShapeGeometry | undefined {
  if (node.type === "vectorNetwork") {
    const paths = vectorNetworkExactRegionPaths(node.network, frame);
    if (paths.length > 0) {
      return { kind: "path", paths };
    }
    const fallback = vectorNetworkPath(node.network, frame);
    return fallback ? { kind: "path", paths: [fallback] } : undefined;
  }
  return nodeGeometry(node, frame);
}

function matteEntryForMask(
  mask: SceneNode,
  frame: FrameNode,
  unsupportedNodeIds: string[],
): VisibleNodeEntry["matte"] | undefined {
  const geometry = nodeMaskGeometry(mask, frame);
  if (!geometry) {
    unsupportedNodeIds.push(mask.id);
    return undefined;
  }
  return {
    id: mask.id,
    name: mask.name || mask.id,
    sample: matteSample(mask, frame, geometry),
  };
}

function primitiveGeometry(node: PrimitiveNode, frame: FrameNode): LottieShapeGeometry | undefined {
  const center = centerForBounds(node.bounds, frame);
  const size = [Math.abs(node.bounds.width), Math.abs(node.bounds.height)];
  if (node.primitive.kind === "oval") {
    const isFullOval = (node.primitive.arcSweep ?? 360) >= 360 && (node.primitive.ratio ?? 0) === 0;
    return isFullOval ? { kind: "ellipse", center, size } : polygonPathGeometry(node, frame);
  }
  if (node.primitive.kind === "rectangle" || node.primitive.kind === "frame") {
    const radius = node.primitive.kind === "rectangle"
      ? Math.max(0, Math.min(...node.primitive.cornerRadii, Math.min(size[0]!, size[1]!) / 2))
      : 0;
    return { kind: "rectangle", center, size, radius };
  }
  return polygonPathGeometry(node, frame);
}

function polygonPathGeometry(node: PrimitiveNode, frame: FrameNode): LottieShapeGeometry | undefined {
  const points = primitiveToPolygon(node);
  if (points.length === 0) {
    return undefined;
  }
  return {
    kind: "path",
    paths: [{
      v: points.map(point => [point.x - frame.bounds.x, point.y - frame.bounds.y]),
      i: points.map(() => [0, 0]),
      o: points.map(() => [0, 0]),
      c: node.primitive.kind !== "line",
    }],
  };
}

function vectorNetworkPaths(network: VectorNetwork, frame: FrameNode): LottiePathValue[] {
  const regionPaths = vectorNetworkExactRegionPaths(network, frame);
  if (regionPaths.length > 0) {
    return regionPaths;
  }
  const path = vectorNetworkPath(network, frame);
  return path ? [path] : [];
}

function vectorNetworkPath(network: VectorNetwork, frame: FrameNode): LottiePathValue | undefined {
  const edge = network.edges[0];
  if (!edge) {
    return undefined;
  }
  const ordered = orderedEdges(network);
  const first = ordered[0];
  if (!first) {
    return undefined;
  }
  const firstVertex = findVertex(network.vertices, first.from.vertexId);
  if (!firstVertex) {
    return undefined;
  }

  const vertices: number[][] = [[firstVertex.x - frame.bounds.x, firstVertex.y - frame.bounds.y]];
  const inTangents: number[][] = [[0, 0]];
  const outTangents: number[][] = [];
  let current = firstVertex;
  let closed = false;

  for (const edge of ordered) {
    const to = findVertex(network.vertices, edge.to.vertexId);
    if (!to) {
      continue;
    }
    outTangents.push(edge.from.handle ? [edge.from.handle.x - current.x, edge.from.handle.y - current.y] : [0, 0]);
    if (to.id === firstVertex.id) {
      inTangents[0] = edge.to.handle ? [edge.to.handle.x - to.x, edge.to.handle.y - to.y] : [0, 0];
      closed = true;
      break;
    }
    vertices.push([to.x - frame.bounds.x, to.y - frame.bounds.y]);
    inTangents.push(edge.to.handle ? [edge.to.handle.x - to.x, edge.to.handle.y - to.y] : [0, 0]);
    current = to;
  }

  while (outTangents.length < vertices.length) {
    outTangents.push([0, 0]);
  }

  return vertices.length > 1 ? { v: vertices, i: inTangents, o: outTangents, c: closed } : undefined;
}

function vectorNetworkStrokePaths(network: VectorNetwork, frame: FrameNode): LottiePathValue[] {
  const paths: LottiePathValue[] = [];
  let vertices: number[][] = [];
  let inTangents: number[][] = [];
  let outTangents: number[][] = [];
  let currentVertexId: string | undefined;
  let subpathStartVertexId: string | undefined;

  const flush = (closed = false): void => {
    while (outTangents.length < vertices.length) {
      outTangents.push([0, 0]);
    }
    if (vertices.length > 1) {
      paths.push({ v: vertices, i: inTangents, o: outTangents, c: closed });
    }
    vertices = [];
    inTangents = [];
    outTangents = [];
    currentVertexId = undefined;
    subpathStartVertexId = undefined;
  };

  for (const edge of network.edges) {
    const from = findVertex(network.vertices, edge.from.vertexId);
    const to = findVertex(network.vertices, edge.to.vertexId);
    if (!from || !to) {
      continue;
    }

    if (currentVertexId !== from.id) {
      flush();
      vertices.push([from.x - frame.bounds.x, from.y - frame.bounds.y]);
      inTangents.push([0, 0]);
      currentVertexId = from.id;
      subpathStartVertexId = from.id;
    }

    const tangents = edgeTangents(edge, from, to, true);
    outTangents.push(tangents.out);
    if (to.id === subpathStartVertexId) {
      inTangents[0] = tangents.in;
      flush(true);
      continue;
    }

    vertices.push([to.x - frame.bounds.x, to.y - frame.bounds.y]);
    inTangents.push(tangents.in);
    currentVertexId = to.id;
  }

  flush();
  return paths;
}

function vectorNetworkExactRegionPaths(network: VectorNetwork, frame: FrameNode): LottiePathValue[] {
  return vectorRegionGeometries(network)
    .filter(geometry => network.regions.length === 0 || geometry.region.filled !== false)
    .flatMap(geometry => lottiePathForEdgeRefs(network, geometry.edgeRefs, frame) ?? []);
}

function lottiePathForEdgeRefs(
  network: VectorNetwork,
  edgeRefs: Array<{ edgeId: string; forward: boolean }>,
  frame: FrameNode,
): LottiePathValue | undefined {
  const firstRef = edgeRefs[0];
  if (!firstRef) {
    return undefined;
  }
  const firstEdge = network.edges.find(edge => edge.id === firstRef.edgeId);
  const firstVertex = firstEdge ? endpointVertex(network, firstEdge, firstRef.forward, "from") : undefined;
  if (!firstEdge || !firstVertex) {
    return undefined;
  }

  const vertices: number[][] = [[firstVertex.x - frame.bounds.x, firstVertex.y - frame.bounds.y]];
  const inTangents: number[][] = [[0, 0]];
  const outTangents: number[][] = [];
  let currentVertex = firstVertex;
  let closed = false;

  for (const ref of edgeRefs) {
    const edge = network.edges.find(candidate => candidate.id === ref.edgeId);
    if (!edge) {
      continue;
    }
    const from = endpointVertex(network, edge, ref.forward, "from");
    const to = endpointVertex(network, edge, ref.forward, "to");
    if (!from || !to) {
      continue;
    }
    if (currentVertex.id !== from.id) {
      break;
    }
    const tangents = edgeTangents(edge, from, to, ref.forward);
    outTangents.push(tangents.out);
    if (to.id === firstVertex.id) {
      inTangents[0] = tangents.in;
      closed = true;
      break;
    }
    vertices.push([to.x - frame.bounds.x, to.y - frame.bounds.y]);
    inTangents.push(tangents.in);
    currentVertex = to;
  }

  while (outTangents.length < vertices.length) {
    outTangents.push([0, 0]);
  }

  return vertices.length > 1 ? { v: vertices, i: inTangents, o: outTangents, c: closed } : undefined;
}

function endpointVertex(network: VectorNetwork, edge: VectorEdge, forward: boolean, endpoint: "from" | "to"): VectorVertex | undefined {
  const vertexId = endpoint === "from"
    ? (forward ? edge.from.vertexId : edge.to.vertexId)
    : (forward ? edge.to.vertexId : edge.from.vertexId);
  return findVertex(network.vertices, vertexId);
}

function edgeTangents(edge: VectorEdge, from: VectorVertex, to: VectorVertex, forward: boolean): { out: number[]; in: number[] } {
  const outHandle = forward ? edge.from.handle : edge.to.handle;
  const inHandle = forward ? edge.to.handle : edge.from.handle;
  return {
    out: outHandle ? [outHandle.x - from.x, outHandle.y - from.y] : [0, 0],
    in: inHandle ? [inHandle.x - to.x, inHandle.y - to.y] : [0, 0],
  };
}

function orderedEdges(network: VectorNetwork): VectorEdge[] {
  const remaining = [...network.edges];
  const ordered: VectorEdge[] = [];
  let current = remaining.shift();
  while (current) {
    ordered.push(current);
    const nextIndex = remaining.findIndex(edge => edge.from.vertexId === current!.to.vertexId);
    current = nextIndex >= 0 ? remaining.splice(nextIndex, 1)[0] : remaining.shift();
  }
  return ordered;
}

function lottieFonts(tracks: LayerTrack[]): LottieFont[] {
  const fonts = new Map<string, LottieFont>();
  for (const track of tracks) {
    const textShape = track.samples.find(sample => sample.shape?.kind === "text")?.shape;
    if (textShape?.kind !== "text") {
      continue;
    }
    const name = textShape.style.fontFamily || "Inter";
    fonts.set(name, {
      fName: name,
      fFamily: name,
      fStyle: String(textShape.style.fontWeight || 400),
      ascent: 75,
    });
  }
  return [...fonts.values()];
}

function lottieLayers(tracks: LayerTrack[], totalFrames: number): LottieLayer[] {
  const layers: LottieLayer[] = [];
  let index = 1;
  for (const track of tracks) {
    if (track.matteTrack) {
      layers.push(shapeLayer(track.matteTrack, index, totalFrames, { td: 1 }));
      index += 1;
      layers.push(shapeLayer(track, index, totalFrames, { tt: 1 }));
      index += 1;
      continue;
    }
    layers.push(shapeLayer(track, index, totalFrames));
    index += 1;
  }
  return layers;
}

function shapeLayer(
  track: LayerTrack,
  index: number,
  totalFrames: number,
  options: { td?: 1; tt?: 1 } = {},
): LottieLayer {
  const firstShape = track.samples.find(sample => sample.shape)?.shape;
  if (firstShape?.kind === "text") {
    return textLayer(track, firstShape, index, totalFrames);
  }
  return {
    ddd: 0,
    ind: index,
    ty: 4,
    nm: track.name,
    ...(options.td ? { td: options.td } : {}),
    ...(options.tt ? { tt: options.tt } : {}),
    sr: 1,
    ks: {
      o: keyframedScalar(track.samples.map(sample => sample.opacity * 100), 0.05),
      r: keyframedScalar(track.samples.map(sample => sample.rotation), 0.05),
      p: keyframedVector(track.samples.map(sample => sample.rotationOrigin), 0.25),
      a: { a: 0, k: [0, 0, 0] },
      s: { a: 0, k: [100, 100, 100] },
    },
    ao: 0,
    shapes: shapeAndPaintItems(track),
    ip: 0,
    op: totalFrames,
    st: 0,
    bm: 0,
  };
}

function textLayer(
  track: LayerTrack,
  firstShape: Extract<LottieShapeGeometry, { kind: "text" }>,
  index: number,
  totalFrames: number,
): LottieTextLayer {
  const firstPainted = track.samples.find(sample => sample.fillColor);
  return {
    ddd: 0,
    ind: index,
    ty: 5,
    nm: track.name,
    sr: 1,
    ks: {
      o: keyframedScalar(track.samples.map(sample => sample.opacity * 100), 0.05),
      r: keyframedScalar(track.samples.map(sample => sample.rotation), 0.05),
      p: keyframedVector(track.samples.map(sample => sample.rotationOrigin), 0.25),
      a: { a: 0, k: [0, 0, 0] },
      s: { a: 0, k: [100, 100, 100] },
    },
    ao: 0,
    t: {
      d: {
        k: [{
          s: textDocument(track, firstShape, firstPainted?.fillColor ?? [0, 0, 0, 1]),
          t: 0,
        }],
      },
      p: {},
      m: {
        g: 1,
        a: { a: 0, k: [0, 0] },
      },
    },
    ip: 0,
    op: totalFrames,
    st: 0,
    bm: 0,
  };
}

function textDocument(track: LayerTrack, firstShape: Extract<LottieShapeGeometry, { kind: "text" }>, color: number[]): LottieTextDocument {
  const firstSample = track.samples.find(sample => sample.shape?.kind === "text");
  const shape = firstSample?.shape?.kind === "text" ? firstSample.shape : firstShape;
  return {
    s: shape.style.fontSize,
    f: shape.style.fontFamily || "Inter",
    t: shape.text,
    j: lottieTextJustification(shape.style.horizontalAlign),
    tr: shape.style.letterSpacing,
    lh: resolvedLineHeight(shape.style),
    ls: 0,
    fc: [color[0] ?? 0, color[1] ?? 0, color[2] ?? 0],
    sz: shape.boxSize,
    ps: [
      shape.boxPosition[0]! - (firstSample?.rotationOrigin[0] ?? 0),
      shape.boxPosition[1]! - (firstSample?.rotationOrigin[1] ?? 0),
    ],
  };
}

function lottieTextJustification(align: TypeStyle["horizontalAlign"]): number {
  if (align === "RIGHT") {
    return 1;
  }
  if (align === "CENTER") {
    return 2;
  }
  if (align === "JUSTIFIED") {
    return 3;
  }
  return 0;
}

function shapeAndPaintItems(track: LayerTrack): LottieShapeItem[] {
  const first = track.samples.find(sample => sample.shape)?.shape;
  const firstPainted = track.samples.find(sample => sample.fillColor || sample.strokeColor);
  const strokePaths = first?.kind === "path" ? first.strokePaths : undefined;
  const separateVectorStroke = first?.kind === "path"
    && firstPainted?.fillColor
    && firstPainted.strokeColor
    && strokePaths
    && strokePaths.length > 0;

  if (!separateVectorStroke) {
    return [...shapeItems(track), ...paintShapes(track)];
  }

  return [
    {
      ty: "gr",
      nm: "Stroke",
      it: [
        ...pathShapeItems(track, "strokePaths"),
        ...paintShapes(track, { fills: false }),
        identityGroupTransform(),
      ],
    },
    {
      ty: "gr",
      nm: "Fill",
      it: [
        ...pathShapeItems(track, "paths"),
        ...paintShapes(track, { strokes: false }),
        identityGroupTransform(),
      ],
    },
  ];
}

function shapeItems(track: LayerTrack): Array<LottieEllipseShape | LottieRectangleShape | LottiePathShape> {
  const samples = track.samples;
  const first = samples.find(sample => sample.shape)?.shape;
  if (first?.kind === "ellipse") {
    return [{
      ty: "el",
      nm: "Ellipse",
      p: keyframedVector(samples.map(sample => relativeEllipseOrRectangleCenter(sample, first.center)), 0.05),
      s: keyframedVector(samples.map(sample => ellipseOrRectangleSize(sample, first.size)), 0.05),
      d: 1,
    }];
  }
  if (first?.kind === "rectangle") {
    return [{
      ty: "rc",
      nm: "Rectangle",
      p: keyframedVector(samples.map(sample => relativeEllipseOrRectangleCenter(sample, first.center)), 0.05),
      s: keyframedVector(samples.map(sample => ellipseOrRectangleSize(sample, first.size)), 0.05),
      r: keyframedScalar(samples.map(sample => sample.shape?.kind === "rectangle" ? sample.shape.radius : first.radius), 0.05),
      d: 1,
    }];
  }
  return pathShapeItems(track, "paths");
}

function pathShapeItems(track: LayerTrack, source: "paths" | "strokePaths"): LottiePathShape[] {
  const samples = track.samples;
  const first = samples.find(sample => sample.shape)?.shape;
  const firstPaths = first?.kind === "path" ? first[source] : undefined;
  const fallbacks = firstPaths && firstPaths.length > 0 ? firstPaths : [emptyPath()];
  return fallbacks.map((fallback, pathIndex) => ({
    ty: "sh",
    nm: fallbacks.length > 1 ? `Path ${pathIndex + 1}` : "Path",
    ks: keyframedPath(samples.map(sample => {
      const paths = sample.shape?.kind === "path" ? sample.shape[source] : undefined;
      return relativePath(sample, paths?.[pathIndex] ?? fallback);
    })),
  }));
}

function paintShapes(track: LayerTrack, options: { fills?: boolean; strokes?: boolean } = {}): Array<LottieFillShape | LottieStrokeShape> {
  const includeFills = options.fills ?? true;
  const includeStrokes = options.strokes ?? true;
  const firstPainted = track.samples.find(sample => sample.fillColor || sample.strokeColor);
  const firstShape = track.samples.find(sample => sample.shape)?.shape;
  const fillRule = firstShape?.kind === "path" && firstShape.fillRule === "evenodd" ? 2 : 1;
  const shapes: Array<LottieFillShape | LottieStrokeShape> = [];
  if (includeFills && firstPainted?.fillColor) {
    shapes.push({
      ty: "fl",
      nm: "Fill",
      c: keyframedVector(track.samples.map(sample => sample.fillColor ?? firstPainted.fillColor!), 0.002),
      o: keyframedScalar(track.samples.map(sample => sample.fillOpacity * 100), 0.05),
      r: fillRule,
    });
  }
  if (includeStrokes && firstPainted?.strokeColor) {
    shapes.push({
      ty: "st",
      nm: "Stroke",
      c: keyframedVector(track.samples.map(sample => sample.strokeColor ?? firstPainted.strokeColor!), 0.002),
      o: keyframedScalar(track.samples.map(sample => sample.strokeOpacity * 100), 0.05),
      w: keyframedScalar(track.samples.map(sample => sample.strokeWidth), 0.05),
      lc: 2,
      lj: 2,
    });
  }
  return shapes;
}

function identityGroupTransform(): LottieTransformShape {
  return {
    ty: "tr",
    p: { a: 0, k: [0, 0] },
    a: { a: 0, k: [0, 0] },
    s: { a: 0, k: [100, 100] },
    r: { a: 0, k: 0 },
    o: { a: 0, k: 100 },
    sk: { a: 0, k: 0 },
    sa: { a: 0, k: 0 },
  };
}

function nodeSample(node: SceneNode, frame: FrameNode, shape: LottieShapeGeometry, options: { fill?: Paint } = {}): RenderSample {
  const fill = options.fill ? solidPaintToLottiePaint(options.fill) : firstSolidPaint(normalizeFillStack(node.style));
  const stroke = firstSolidStroke(normalizeStrokeStack(node.style));
  return {
    frameIndex: 0,
    shape,
    opacity: finite01(node.style.opacity ?? 1),
    rotation: finiteNumber(node.transform.rotation, 0),
    rotationOrigin: rotationOriginForNode(node, frame),
    fillColor: fill ? lottieColor(fill.color) : undefined,
    fillOpacity: fill ? finite01(fill.color[3] ?? 1) : 0,
    strokeColor: stroke ? lottieColor(stroke.color) : undefined,
    strokeOpacity: stroke ? finite01(stroke.color[3] ?? 1) : 0,
    strokeWidth: stroke?.width ?? 0,
  };
}

function matteSample(node: SceneNode, frame: FrameNode, shape: LottieShapeGeometry): RenderSample {
  const sample = nodeSample(node, frame, shape);
  const strokeWidth = Math.max(0, sample.strokeWidth);
  return {
    ...sample,
    opacity: 1,
    fillColor: [1, 1, 1, 1],
    fillOpacity: 1,
    strokeColor: strokeWidth > 0 ? [1, 1, 1, 1] : undefined,
    strokeOpacity: strokeWidth > 0 ? 1 : 0,
    strokeWidth,
  };
}

function emptySample(frameIndex: number): RenderSample {
  return {
    frameIndex,
    opacity: 0,
    rotation: 0,
    rotationOrigin: [0, 0, 0],
    fillOpacity: 0,
    strokeOpacity: 0,
    strokeWidth: 0,
  };
}

function firstSolidPaint(paints: Paint[]): { color: number[] } | undefined {
  const paint = paints.find(paint => paint.type === "solid");
  return paint ? solidPaintToLottiePaint(paint) : undefined;
}

function solidPaintToLottiePaint(paint: Paint): { color: number[] } | undefined {
  return paint.type === "solid" ? { color: oklchToRgba(paint.color) } : undefined;
}

function firstSolidStroke(strokes: StrokeStyle[]): { color: number[]; width: number } | undefined {
  const stroke = strokes.find(stroke => stroke.paint.type === "solid" && stroke.width > 0);
  return stroke?.paint.type === "solid" ? { color: oklchToRgba(stroke.paint.color), width: stroke.width } : undefined;
}

function lottieColor(rgba: number[]): number[] {
  return [finite01(rgba[0] ?? 0), finite01(rgba[1] ?? 0), finite01(rgba[2] ?? 0), finite01(rgba[3] ?? 1)];
}

function centerForBounds(bounds: SceneNode["bounds"], frame: FrameNode): number[] {
  return [
    bounds.x - frame.bounds.x + bounds.width / 2,
    bounds.y - frame.bounds.y + bounds.height / 2,
  ];
}

function relativeEllipseOrRectangleCenter(sample: RenderSample, fallback: number[]): number[] {
  const center = ellipseOrRectangleCenter(sample, fallback);
  return [
    center[0]! - sample.rotationOrigin[0]!,
    center[1]! - sample.rotationOrigin[1]!,
  ];
}

function ellipseOrRectangleCenter(sample: RenderSample, fallback: number[]): number[] {
  if (sample.shape?.kind === "ellipse" || sample.shape?.kind === "rectangle") {
    return sample.shape.center;
  }
  return fallback;
}

function ellipseOrRectangleSize(sample: RenderSample, fallback: number[]): number[] {
  if (sample.shape?.kind === "ellipse" || sample.shape?.kind === "rectangle") {
    return sample.shape.size;
  }
  return fallback;
}

function relativePath(sample: RenderSample, path: LottiePathValue): LottiePathValue {
  return {
    ...path,
    v: path.v.map(point => [
      point[0]! - sample.rotationOrigin[0]!,
      point[1]! - sample.rotationOrigin[1]!,
    ]),
  };
}

function rotationOriginForNode(node: SceneNode, frame: FrameNode): number[] {
  const origin = transformRotationOrigin(node.bounds, node.transform);
  return [
    origin.x - frame.bounds.x,
    origin.y - frame.bounds.y,
    0,
  ];
}

function keyframedScalar(values: number[], tolerance = 0.01): LottieScalarAnimatable {
  const normalized = values.map(value => round(finiteNumber(value, 0)));
  if (valuesAreEqual(normalized)) {
    return { a: 0, k: normalized[0] ?? 0 };
  }
  const simplified = simplifyTimedValues(normalized.map(value => [value]), tolerance)
    .map(point => ({ t: point.t, value: point.value[0] ?? 0 }));
  return { a: 1, k: scalarKeyframes(simplified) };
}

function keyframedVector(values: number[][], tolerance = 0.01): LottieVectorAnimatable {
  const normalized = values.map(value => value.map(channel => round(finiteNumber(channel, 0))));
  if (vectorsAreEqual(normalized)) {
    return { a: 0, k: normalized[0] ?? [] };
  }
  const simplified = simplifyTimedValues(normalized, tolerance);
  return { a: 1, k: keyframes(simplified) };
}

function keyframedPath(values: LottiePathValue[]): LottiePathAnimatable {
  const normalized = values.map(path => normalizePath(path));
  if (pathsAreEqual(normalized)) {
    return { a: 0, k: normalized[0] ?? emptyPath() };
  }
  if (!pathsShareTopology(normalized)) {
    return { a: 1, k: keyframes(normalized.map((path, index) => ({ t: index, value: [path] }))) };
  }

  const template = normalized[0] ?? emptyPath();
  const simplified = simplifyTimedValues(normalized.map(pathToNumbers), 0.25)
    .map(point => ({ t: point.t, value: [numbersToPath(point.value, template)] }));
  return { a: 1, k: keyframes(simplified) };
}

function keyframes<T>(values: Array<{ t: number; value: T }>): LottieKeyframe<T>[] {
  return values.map((entry, index) => ({
    t: entry.t,
    s: entry.value,
    ...(index < values.length - 1 ? { e: values[index + 1]!.value } : {}),
    i: LINEAR_IN,
    o: LINEAR_OUT,
  }));
}

function scalarKeyframes(values: Array<{ t: number; value: number }>): LottieKeyframe<number[]>[] {
  return values.map((entry, index) => ({
    t: entry.t,
    s: [entry.value],
    ...(index < values.length - 1 ? { e: [values[index + 1]!.value] } : {}),
    i: LINEAR_IN,
    o: LINEAR_OUT,
  }));
}

function normalizePath(path: LottiePathValue): LottiePathValue {
  return {
    c: path.c,
    v: path.v.map(point => point.map(value => round(finiteNumber(value, 0)))),
    i: path.i.map(point => point.map(value => round(finiteNumber(value, 0)))),
    o: path.o.map(point => point.map(value => round(finiteNumber(value, 0)))),
  };
}

function valuesAreEqual(values: number[]): boolean {
  return values.every(value => value === values[0]);
}

function vectorsAreEqual(values: number[][]): boolean {
  return values.every(value => JSON.stringify(value) === JSON.stringify(values[0]));
}

function pathsAreEqual(values: LottiePathValue[]): boolean {
  return values.every(value => JSON.stringify(value) === JSON.stringify(values[0]));
}

function pathsShareTopology(values: LottiePathValue[]): boolean {
  const first = values[0];
  if (!first) {
    return true;
  }
  return values.every(path =>
    path.c === first.c &&
    path.v.length === first.v.length &&
    path.i.length === first.i.length &&
    path.o.length === first.o.length,
  );
}

function pathToNumbers(path: LottiePathValue): number[] {
  return [...path.v, ...path.i, ...path.o].flatMap(point => [point[0] ?? 0, point[1] ?? 0]);
}

function numbersToPath(values: number[], template: LottiePathValue): LottiePathValue {
  let index = 0;
  const readPoints = (count: number): number[][] => Array.from({ length: count }, () => {
    const point = [values[index] ?? 0, values[index + 1] ?? 0];
    index += 2;
    return point;
  });
  return {
    c: template.c,
    v: readPoints(template.v.length),
    i: readPoints(template.i.length),
    o: readPoints(template.o.length),
  };
}

function simplifyTimedValues(values: number[][], tolerance: number): Array<{ t: number; value: number[] }> {
  if (values.length <= 2) {
    return values.map((value, index) => ({ t: index, value }));
  }
  const keep = new Set<number>([0, values.length - 1]);
  simplifyRange(values, 0, values.length - 1, Math.max(0, tolerance), keep);
  return [...keep]
    .sort((left, right) => left - right)
    .map(index => ({ t: index, value: values[index]! }));
}

function simplifyRange(values: number[][], start: number, end: number, tolerance: number, keep: Set<number>): void {
  if (end <= start + 1) {
    return;
  }
  let worstIndex = -1;
  let worstError = -1;
  for (let index = start + 1; index < end; index += 1) {
    const error = interpolationError(values, start, end, index);
    if (error > worstError) {
      worstError = error;
      worstIndex = index;
    }
  }
  if (worstError <= tolerance || worstIndex < 0) {
    return;
  }
  keep.add(worstIndex);
  simplifyRange(values, start, worstIndex, tolerance, keep);
  simplifyRange(values, worstIndex, end, tolerance, keep);
}

function interpolationError(values: number[][], start: number, end: number, index: number): number {
  const span = Math.max(1, end - start);
  const u = (index - start) / span;
  const from = values[start] ?? [];
  const to = values[end] ?? [];
  const current = values[index] ?? [];
  const channelCount = Math.max(from.length, to.length, current.length);
  let maxError = 0;
  for (let channel = 0; channel < channelCount; channel += 1) {
    const expected = lerp(from[channel] ?? 0, to[channel] ?? 0, u);
    maxError = Math.max(maxError, Math.abs((current[channel] ?? 0) - expected));
  }
  return maxError;
}

function lerp(from: number, to: number, u: number): number {
  return from + (to - from) * u;
}

function emptyPath(): LottiePathValue {
  return { v: [], i: [], o: [], c: false };
}

function isPotentiallyVisibleLeaf(node: SceneNode): boolean {
  return node.type === "shape" || node.type === "boolean" || node.type === "text";
}

function resolveBaseFrame(document: CurvaDocument, frameId: string | undefined, rootNodeId: string | undefined): FrameNode {
  return resolveFrame(document, frameId ?? animationFrameId(document.animation, rootNodeId));
}

function resolveFrame(document: CurvaDocument, frameId: string | undefined): FrameNode {
  const frame = frameId ? findSceneNode(document, frameId) : findFirstFrameNode(document);
  if (frame?.type !== "frame") {
    throw new Error("No frame available for Lottie export.");
  }
  return frame;
}

function findVertex(vertices: VectorVertex[], vertexId: string): VectorVertex | undefined {
  return vertices.find(vertex => vertex.id === vertexId);
}

function normalizedFps(fps: number | undefined): number {
  return Math.max(1, Math.min(120, Math.round(fps ?? DEFAULT_FPS)));
}

function frameTimeMs(frame: number, fps: number, durationMs: number): number {
  return Math.min(durationMs, (frame / fps) * 1000);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new Error("Lottie export cancelled");
  }
}

function finiteNumber(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function finite01(value: number): number {
  return Math.max(0, Math.min(1, finiteNumber(value, 0)));
}

function round(value: number): number {
  return Number(value.toFixed(4));
}
